/**
 * Talos AI Knowledge & Reasoning Engine
 * Inteligência contextual e raciocínio especializado em todas as camadas da plataforma MUFT:
 * ERP, PDV, CRM, Ponto Digital, Permissões/Perfis (Gerente vs Vendedor), Fiscal, Contábil e Planos.
 */

const normalize = (text) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s]/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

export function generateTalosResponse(rawQuery) {
  const q = normalize(rawQuery);
  const words = q.split(" ");

  // Helper para verificar presença de palavras/expressões
  const has = (...terms) => terms.some((t) => q.includes(normalize(t)));
  const hasAll = (...terms) => terms.every((t) => q.includes(normalize(t)));

  // 1. PERFIS DE ACESSO, DISTINÇÃO DE LOGIN, GERENTE VS VENDEDOR, PERMISSÕES
  if (
    has("distincao", "diferenca", "separar", "distinguir", "dividir") &&
    has("login", "gerente", "vendedor", "usuario", "perfil", "acesso", "permissao")
  ) {
    return (
      "No MUFT, a distinção entre gerente e vendedor é feita por meio de **Perfis e Permissões de Acesso Granulares**:\n\n" +
      "• **Perfil de Vendedor / Operador de PDV**:\n" +
      "  - Acesso direto à Frente de Caixa (PDV) com login por e-mail ou **PIN individual rápido** no terminal compartilhado.\n" +
      "  - Realiza vendas, consultas de produtos, leitura de código de barras e emissão de cupons/NF-e.\n" +
      "  - **Bloqueado para**: relatórios financeiros globais, margem de lucro, DRE, alteração de regras fiscais e cancelamento de vendas sem autorização.\n\n" +
      "• **Perfil de Gerente / Administrador**:\n" +
      "  - Visão completa de relatórios, controle de caixa (sangria/suprimento), DRE e fluxo financeiro de todas as filiais.\n" +
      "  - Permissão para autorizar descontos especiais, estornos, cadastro de colaboradores e gestão de ponto.\n\n" +
      "Você configura e altera os papéis de cada colaborador a qualquer momento no menu *Configurações > Equipe e Permissões* do MUFT APP."
    );
  }

  if (
    has("permissao", "permissoes", "perfil de acesso", "niveis de acesso", "bloquear vendedor", "senha de gerente", "pin do vendedor")
  ) {
    return (
      "O MUFT possui controle total de **Níveis de Acesso e Permissões**:\n\n" +
      "1. Cada colaborador recebe seu próprio login ou PIN com permissões customizadas por módulo (ERP, PDV, CRM, Ponto Digital e Contabilidade).\n" +
      "2. O administrador pode definir quem visualiza custos, quem pode dar desconto, quem fecha caixa e quem tem acesso aos relatórios contábeis.\n" +
      "3. Lembre-se: você pode criar quantos perfis e cadastrar quantos funcionários precisar, pois **usuários e equipe são 100% ilimitados** em todos os planos."
    );
  }

  // 2. CADASTRO DE EQUIPE, LIMITE DE USUÁRIOS (pergunta explícita de limite)
  if (
    has("limite de usuario", "limite de usuarios", "limite de funcionario", "limite de pessoa", "quantos usuarios", "taxa por usuario", "pagar por usuario", "adicional por funcionario") ||
    (has("posso cadastrar", "posso colocar", "quantos posso") && has("colaboradores", "funcionarios", "vendedores", "equipe", "usuarios"))
  ) {
    return (
      "Sim! No MUFT você pode cadastrar toda a sua equipe sem nenhum tipo de limite ou cobrança adicional. " +
      "**Usuários, funcionários, vendedores, colaboradores, produtos e clientes são 100% ILIMITADOS em todos os planos**.\n\n" +
      "Você nunca pagará taxa extra por adicionar um novo vendedor no balcão, operador no caixa ou funcionário no ponto. " +
      "A única variação entre os planos é o número de filiais da sua empresa (de 1 até 30 filiais)."
    );
  }

  // 3. MULTI-FILIAL, EXPANSÃO DE LOJAS, TRANSFERÊNCIA ENTRE UNIDADES
  if (
    has("multi filial", "multi-filial", "outra filial", "adicionar filial", "transferencia entre filiais", "estoque entre lojas", "varias lojas")
  ) {
    return (
      "O MUFT conta com arquitetura **Multi-Filial nativa** (de 1 até 30 filiais):\n\n" +
      "• **Visão Unificada ou por Loja**: O gestor acompanha vendas, fluxo de caixa e DRE consolidado de toda a rede ou isolado por unidade.\n" +
      "• **Estoque e Transferências**: Controle de estoque independente por filial com módulo ágil de transferência de mercadorias.\n" +
      "• **Ponto com Cerca Virtual (GPS)**: Cada colaborador bate ponto vinculado à coordenada geográfica da sua filial designada.\n" +
      "• **Regras Fiscais por Estado**: Configuração de alíquotas e inscrições estaduais específicas por CNPJ filial."
    );
  }

  // 4. PLANOS, PREÇOS E MENSALIDADES
  if (
    has("plano", "planos", "preco", "precos", "quanto custa", "valor", "valores", "mensalidade", "tabela de preco", "custo mensal")
  ) {
    return (
      "A MUFT trabalha com **6 planos mensais transparentes** com todos os módulos (ERP, PDV, CRM, Ponto Digital e Contabilidade) e usuários ilimitados:\n\n" +
      "• **Essencial**: 1 filial — **R$ 230/mês** (R$ 230/filial)\n" +
      "• **Crescimento**: até 3 filiais — **R$ 660/mês** (R$ 220/filial)\n" +
      "• **Profissional** *(Mais Escolhido / Recomendado)*: até 5 filiais — **R$ 1.075/mês** (R$ 215/filial)\n" +
      "• **Empresarial**: até 10 filiais — **R$ 2.100/mês** (R$ 210/filial)\n" +
      "• **Expansão**: até 20 filiais — **R$ 4.100/mês** (R$ 205/filial)\n" +
      "• **Grande Empresa**: até 30 filiais — **R$ 6.000/mês** (R$ 200/filial)\n\n" +
      "Assinatura 100% mensal, sem taxa de adesão e sem contratos de fidelidade."
    );
  }

  // 5. FISCAL, NF-E, NFC-E, SEFAZ, REFORMA TRIBUTÁRIA, TRIBUTAÇÃO
  if (
    has("fiscal", "nfe", "nf-e", "nfce", "nfc-e", "sefaz", "tribut", "imposto", "impostos", "reforma tributaria", "ibs", "cbs", "simples nacional", "regime normal", "lucro presumido", "cfop", "ncm", "cest", "certificado digital", "certificado a1")
  ) {
    return (
      "O motor fiscal do MUFT é especialista em legislação tributária brasileira e automatiza suas emissões:\n\n" +
      "• **Emissão Completa**: Suporte nativo a **NF-e (modelo 55)** e **NFC-e (modelo 65)** para Simples Nacional e Regime Normal.\n" +
      "• **Certificado Digital A1**: Instalação simplificada e validação em tempo real com a SEFAZ.\n" +
      "• **Inteligência de Enquadramento**: Eu (Talos) analiso o código de barras (EAN) ou NCM e sugiro CFOP, ICMS, PIS, COFINS e o novo padrão da **Reforma Tributária (IBS/CBS)**.\n" +
      "• **Auditoria de Rejeições**: Traduzo erros técnicos da SEFAZ em português simples e aponto o campo exato para correção imediata.\n" +
      "• **Exportação Contábil**: Geração de XMLs e DANFEs em lote para envio direto ao seu contador."
    );
  }

  // 6. CONTABILIDADE, DRE, CONTROLADORIA, FECHAMENTO CONTÁBIL
  if (
    has("contabil", "contabilidade", "contador", "dre", "margem liquida", "margem bruta", "cmv", "fechamento contabil", "plano de contas", "centro de custo", "balanco")
  ) {
    return (
      "Sim! Sou especialista em **contabilidade gerencial e controladoria do MUFT**:\n\n" +
      "• **DRE em Tempo Real**: Apura Receita Bruta, deduções, impostos sobre vendas, CMV (Custo de Mercadoria Vendida), despesas operacionais e margem líquida consolidada ou por filial.\n" +
      "• **Centros de Custo & Plano de Contas**: Categorização automática de despesas e receitas conforme a estrutura contábil da sua empresa.\n" +
      "• **Fechamento para o Contador**: Relatórios de conciliação bancária, fechamentos de caixa e arquivos fiscais prontos para envio ao escritório de contabilidade.\n" +
      "• **Análise Financeira**: Você pode me perguntar a qualquer momento qual foi o CMV ou a margem líquida de qualquer filial e eu calculo na hora."
    );
  }

  // 7. PONTO DIGITAL, BIOMETRIA FACIAL, MTP PORTARIA 671, JORNADA
  if (
    has("ponto", "ponto digital", "facial", "biometria", "selfie", "gps", "geolocalizacao", "geocerca", "portaria 671", "mtp", "hora extra", "horas extras", "banco de horas", "espelho de ponto", "atrasos")
  ) {
    return (
      "O **Ponto Digital MUFT** atende rigorosamente a **Portaria 671 do Ministério do Trabalho (MTP)**:\n\n" +
      "• **Selfie com Biometria Facial**: O colaborador bate o ponto pelo celular e o sistema valida a biometria contra a foto cadastrada.\n" +
      "• **Geolocalização (GPS) com Geofencing**: Confirmação se o colaborador está dentro do raio físico permitido da filial.\n" +
      "• **Auditoria Inteligente**: Eu (Talos) identifico atrasos, horas extras e inconsistências de forma automática.\n" +
      "• **Espelho de Ponto & Banco de Horas**: Relatórios consolidados para o RH e folha de pagamento em poucos cliques."
    );
  }

  // 8. PDV, FRENTE DE CAIXA, MODO OFFLINE, SANGRIA, SUPRIMENTO
  if (
    has("pdv", "frente de caixa", "caixa", "sangria", "suprimento", "abrir caixa", "fechar caixa", "offline", "cupom", "balcao", "venda rapida")
  ) {
    return (
      "O **PDV MUFT** foi projetado para alta velocidade de balcão:\n\n" +
      "• **Operação Ágil**: Abertura, fechamento de caixa, sangria, suprimento e terminal compartilhado com PIN por vendedor.\n" +
      "• **Modo Offline**: Se a internet cair, o PDV continua vendendo normalmente e sincroniza tudo assim que a conexão retorna.\n" +
      "• **Identificação Rápida**: Leitura de código de barras pela câmera do smartphone ou leitor USB/Bluetooth.\n" +
      "• **Múltiplos Pagamentos**: Pix instantâneo, cartões, dinheiro e vinculação de CPF/CNPJ na nota com um toque."
    );
  }

  // 9. CADASTRO POR FOTO (TALOS VISION), ESTOQUE, CÓDIGO DE BARRAS
  if (
    has("foto", "camera", "codigo de barra", "ean", "cadastrar produto", "cadastro de produto", "leitor de codigo", "visao")
  ) {
    return (
      "Com o **Talos Vision**, o cadastro de produtos é instantâneo:\n\n" +
      "1. Aponte a câmera para o produto ou código de barras (EAN).\n" +
      "2. Eu identifico o nome, marca, categoria e sugiro toda a estrutura tributária (NCM, CEST, CFOP, alíquotas).\n" +
      "3. O item fica disponível imediatamente para venda no PDV e sincronizado com o estoque de todas as filiais.\n" +
      "4. Se estiver sem conexão, você pode cadastrar manualmente sem travar a loja."
    );
  }

  // 10. CRM, VENDAS, FUNIL, CLIENTES, WHATSAPP, META
  if (
    has("crm", "cliente", "clientes", "funil de vendas", "whatsapp", "meta", "provedor de tecnologia", "recuperar cliente", "aniversariante", "pos venda", "disparo")
  ) {
    return (
      "A MUFT é um **Provedor de Tecnologia Meta oficial** e nosso módulo de CRM possui conexão nativa do WhatsApp Business em 1 clique:\n\n" +
      "• **WhatsApp Business Oficial (Cloud API)**: Conexão direta e segura homologada pela Meta, sem depender de emuladores, QR codes instáveis ou risco de bloqueio de chip.\n" +
      "• **Disparos em 1 Clique**: Envio de promoções, lembretes de cobrança com Pix Copia e Cola e mensagens de pós-venda direto pelo app.\n" +
      "• **Segmentação Inteligente**: O Talos identifica automaticamente clientes inativos (sumidos), clientes VIPs e aniversariantes do mês para ativação comercial.\n" +
      "• **Histórico Unificado no PDV**: Cada compra registrada no balcão alimenta a timeline do cliente em tempo real."
    );
  }

  // 11. CÓDIGO, ARQUITETURA, SEGURANÇA E DESENVOLVIMENTO
  if (
    has("codigo", "arquitetura", "tecnologia", "desenvolvido", "banco de dados", "stack", "seguranca", "api", "supabase", "react")
  ) {
    return (
      "A arquitetura do **MUFT** foi construída para alta disponibilidade e integridade de dados:\n\n" +
      "• **Banco de Dados Unificado**: ERP, PDV, CRM, Ponto e Contabilidade rodam na mesma base de dados, eliminando integrações lentas ou dados duplicados.\n" +
      "• **Stack Tecnológica**: Frontend ultra-otimizado em React e Tailwind CSS, backend em FastAPI/Python com Supabase e criptografia ponta a ponta.\n" +
      "• **Camada de Inteligência Talos**: Motor de raciocínio operacional nativo que processa comandos de negócios, validações fiscais e auditorias em tempo real."
    );
  }

  // 12. APRESENTAÇÃO DO TALOS
  if (
    has("quem e voce", "quem e o talos", "o que voce faz", "suas funcoes", "talos ia", "ajuda", "capacidades")
  ) {
    return (
      "Eu sou o **Talos**, a inteligência artificial especialista da plataforma MUFT ERP/PDV/CRM/Ponto Digital/Contabilidade.\n\n" +
      "Minha função é operar o sistema com você: consultar caixas, analisar estoque, auditar regras fiscais e SEFAZ, " +
      "gerenciar o ponto com biometria facial, gerar relatórios de DRE e responder qualquer dúvida operacional da sua empresa. " +
      "Como posso te ajudar agora?"
    );
  }

  // 13. RESPOSTA DE CONTEXTO GERAL
  return (
    `Compreendi sua dúvida sobre "${rawQuery}". ` +
    "No **MUFT**, você tem uma plataforma All-in-One completa que unifica **ERP, PDV, CRM, Ponto Digital e Contabilidade** com **equipe e usuários 100% ilimitados**. " +
    "Posso te detalhar permissões de acesso (gerente vs vendedor), controle multi-filial (de 1 a 30 lojas), emissão fiscal (NF-e/NFC-e), fechamento contábil (DRE) ou nossos 6 planos mensais. " +
    "Sobre qual destes temas você gostaria de saber mais?"
  );
}
